// Sprint 17 — Guided tours (PT/EN). One source of truth, keyed by pageKey.
const T = {
  dashboard: {
    pt: { title: "Início — Sua visão geral",
      steps: [
        { title: "Boas-vindas", body: "Esta é sua tela inicial. O banner mostra seu nome, os valores Sagewood e o selo dos 25 anos." },
        { title: "Alertas inteligentes", body: "Logo abaixo do banner aparecem alertas inteligentes (treinos vencendo, aprovações pendentes, eventos próximos)." },
        { title: "KPIs", body: "Os cards mostram saldo de PTO/Férias, suas solicitações abertas e o que aguarda sua aprovação." },
        { title: "Ações rápidas", body: "Use os atalhos para abrir rapidamente os fluxos mais usados (PTO, Reembolsos, Treinos)." },
      ] },
    en: { title: "Home — Your overview",
      steps: [
        { title: "Welcome", body: "This is your home screen. The banner shows your name, the Sagewood values, and the 25-years badge." },
        { title: "Smart Alerts", body: "Below the banner you'll find smart alerts (overdue trainings, pending approvals, upcoming events)." },
        { title: "KPIs", body: "Cards show your PTO/Vacation balance, your open requests and items awaiting your approval." },
        { title: "Quick actions", body: "Use the shortcuts to quickly jump into the most-used flows (PTO, Reimbursements, Trainings)." },
      ] },
  },
  planner: {
    pt: { title: "Planner — Sua agenda",
      steps: [
        { title: "Visão calendário", body: "Veja o mês todo com eventos por cor: PTO, treinos, avaliações, follow-up 90 dias, terminations e lembretes." },
        { title: "Filtros", body: "Alterne entre Meu / Time / Empresa. RH/admin têm filtros adicionais por integrante, departamento e nome." },
        { title: "Lembretes próprios", body: 'Clique em "Novo lembrete" para criar lembretes pessoais ou (RH) para outro integrante.' },
        { title: "Detalhe + link", body: "Clique em qualquer evento para abrir o detalhe com link direto à página de origem." },
      ] },
    en: { title: "Planner — Your agenda",
      steps: [
        { title: "Calendar view", body: "See the full month with color-coded events: PTO, trainings, evaluations, 90-day follow-ups, terminations and reminders." },
        { title: "Filters", body: "Toggle Mine / Team / Company scope. HR/admin get extra filters by team member, department and name." },
        { title: "Custom reminders", body: 'Click "New reminder" to create your own (or HR can create for any teammate).' },
        { title: "Detail + jump", body: "Click any event to open detail with a link to its source page." },
      ] },
  },
  pto: {
    pt: { title: "PTO & Férias",
      steps: [
        { title: "Solicitar", body: "Clique em Nova solicitação, escolha tipo, datas e motivo. Vai direto pro manager aprovar." },
        { title: "Comentários", body: "Clique em uma linha para abrir o detalhe e conversar com o aprovador via thread de comentários." },
        { title: "Saldos", body: "Saldo PTO e Férias aparecem no cabeçalho. O sistema valida automaticamente disponibilidade." },
      ] },
    en: { title: "PTO & Vacation",
      steps: [
        { title: "Request", body: "Click New request, pick type, dates and reason. Goes straight to your manager." },
        { title: "Comments", body: "Click a row to open detail and chat with approver via comments thread." },
        { title: "Balances", body: "PTO and Vacation balances are shown at the top. The system validates availability automatically." },
      ] },
  },
  travel: {
    pt: { title: "Despesas do integrante",
      steps: [
        { title: "Política Sagewood", body: "Cada item é validado contra a política (refeições, hospedagem, transporte, milhagem). Limites podem variar por cargo (CEO/HR/Manager)." },
        { title: "Itens", body: "Adicione múltiplos itens em uma única viagem. Anexe recibo (foto ou PDF) em cada um." },
        { title: "Violações", body: "Se algum item exceder o limite, fica destacado em vermelho na hora do envio — você ainda pode submeter, mas o aprovador verá o motivo." },
      ] },
    en: { title: "Travel Expenses · Team member flow",
      steps: [
        { title: "Welcome", body: "Submit, track and resubmit your travel expenses here. The whole flow takes ~2 minutes per trip. Let's walk through it." },
        { title: "Step 1 · Create a new expense", body: 'Click the "+ New expense" button at the top of the page. The form asks for trip purpose, destination, departure and return dates, plus optional project / cost code.' },
        { title: "Step 2 · Add line items", body: "Inside the form add one row per expense (meal, lodging, transport, mileage, etc.). Each line needs a category, date, amount, currency, cost center and a RECEIPT attachment to submit. Foreign currencies auto-convert to USD using daily FX rates." },
        { title: "Step 3 · Submit or save as draft", body: '"Submit" sends it to your manager (receipts required, full validation). "Save as draft" lets you keep editing later (only date + amount required).' },
        { title: "Step 4 · Track your expenses", body: "Your expense cards appear in the list below. Each card shows the current status: draft, pending approval, approved, submitted to QBO, paid, or rejected. Click any card to see details and the approval chain." },
        { title: "Step 5 · Resubmit a partially-rejected expense", body: "If your manager rejects some items, the system automatically creates a NEW DRAFT containing only those items. You'll see an amber 'Rejected · Adjust' chip on its card. Open it — each rejected item shows the manager's reason — fix the amount or receipt, then click Resubmit." },
        { title: "Need help?", body: "Contact HR at ana.hr@sagewood.com. You can replay this tour anytime via the help icon in the page header." },
      ] },
    en_manager: { title: "Travel Expenses · Manager flow",
      steps: [
        { title: "Welcome, Manager", body: "Here you'll review and approve your team's travel expenses — line by line. Let's walk through the flow." },
        { title: "Step 1 · Find pending expenses", body: "Your direct reports' pending expenses appear in this list. The Manager Cockpit (sidebar → 'Manager cockpit') also shows them in the 'Needs you now' action queue with urgency labels." },
        { title: "Step 2 · Open the card", body: "Click any card with status 'pending approval' to open the review modal. You'll see the trip summary, every line item, attached receipts (open in a new tab via the paperclip icon), and the approval chain progress." },
        { title: "Step 3 · Decide per item", body: 'Use "Decide per item" (recommended). For EACH line you can approve or reject, with an optional comment. You must decide every item before submitting — partial decisions are blocked.' },
        { title: "Step 4 · What happens after you decide", body: "• All approved → advances to the next approver in the chain (or 'approved' if you're the last).\n• All rejected → expense closes as 'rejected'.\n• Mixed → the doc splits: approved items keep moving in the chain; rejected items spawn a NEW DRAFT for the team member with YOUR comment embedded per line, so they know exactly what to fix." },
        { title: "Step 5 · Separation of duties", body: "You cannot approve your own expense. You only act on steps where your role is the current approver in the chain. The Cockpit lets you switch between 'direct team' and 'extended team' scope." },
        { title: "Done!", body: "Open the Manager Cockpit for KPIs, 1:1 history and risk flags on your team. Replay this tour anytime via the help icon." },
      ] },
    en_hr: { title: "Travel Expenses · HR / Finance flow",
      steps: [
        { title: "Welcome, HR / Finance", body: "You finalize approved expenses and push them to QuickBooks Online. Let's walk through the closing flow." },
        { title: "Step 1 · Final approval (if in the chain)", body: "If your approval chain ends at 'hr', expenses approved by the manager land in your queue. Open the card to review the full audit trail of per-item decisions and apply your final decision." },
        { title: "Step 2 · Sync to QBO", body: 'Once an expense is fully "approved", click "Sync QBO" inside the modal. The system creates the employee as a QBO Vendor (if needed) and posts a Bill payable to them, mapping each line to the right account via Admin → QBO Mapping.' },
        { title: "Step 3 · Multi-currency handling", body: "Foreign currency lines are converted to USD using the daily Frankfurter rate. If FX is temporarily unavailable, the expense is flagged 'fx_pending=true' — you'll see an alert, and conversion happens on the next sync attempt." },
        { title: "Step 4 · Payment status", body: "After sync the status moves to 'submitted_to_qbo'. A background scheduler polls QBO every 10 minutes — once the Bill is marked paid in QBO, the status here flips to 'paid' automatically. You can also mark manually if needed." },
        { title: "Step 5 · Configuration (Admin tab)", body: "In Admin → Travel Policies you set per-category limits (per-trip or per-day), enforcement mode (strict / warn / off) and role exemptions. Travel Expense Types lets you add/edit/remove categories. QBO Mapping connects each category to a QBO Chart-of-Accounts node." },
        { title: "Step 6 · Audit trail", body: "Every per-item decision (who, when, comment), every QBO call (request/response) and every FX conversion is logged. Open any expense card to see the full chain. Export to CSV for compliance reports." },
        { title: "Ready to launch", body: "Configure your policies first, walk your team through their own tour, then go live. Replay this anytime via the help icon." },
      ] },
  },
  reimbursements: {
    pt: { title: "Reembolsos de projeto",
      steps: [
        { title: "Só projeto", body: "Use esta página para custos de projeto (material, serviço com cost code)." },
        { title: "Despesas pessoais?", body: "Para refeições, hospedagem, milhagem etc., use Despesas do integrante (política Sagewood automática)." },
      ] },
    en: { title: "Project reimbursements",
      steps: [
        { title: "Project only", body: "Use this page for project costs (material, service with cost code)." },
        { title: "Personal expenses?", body: "For meals, lodging, mileage etc., use Team member Expenses (Sagewood policy applied automatically)." },
      ] },
  },
  trainings: {
    pt: { title: "Trainings",
      steps: [
        { title: "Meus treinos", body: "Mostra o que está atribuído a você, com status (pendente, concluído, atrasado)." },
        { title: "Catálogo", body: "Explore todos os treinos disponíveis. Clique em um card para ver descrição completa, conteúdo e progresso de turma." },
        { title: "Team compliance", body: "Managers veem o status de compliance do time direto. RH/admin podem atribuir treinos manualmente." },
      ] },
    en: { title: "Trainings",
      steps: [
        { title: "My trainings", body: "Shows what's assigned to you with status (pending, completed, overdue)." },
        { title: "Catalog", body: "Browse all available trainings. Click a card to see full description, content and class progress." },
        { title: "Team compliance", body: "Managers see direct reports compliance. HR/admin can manually assign trainings." },
      ] },
  },
  directory: {
    pt: { title: "Diretório",
      steps: [
        { title: "Cards de integrantes", body: "Clique em qualquer card para abrir o perfil completo (cargo, departamento, contato, aniversário…)." },
        { title: "Aniversariantes", body: "Os aniversariantes do mês aparecem em destaque no topo da página." },
        { title: "Ex-integrantes", body: 'Use a tab "Former team members" para acessar histórico de quem já saiu.' },
      ] },
    en: { title: "Directory",
      steps: [
        { title: "Team member cards", body: "Click any card to open the full profile (job title, department, contact, birthday…)." },
        { title: "Birthdays", body: "This month's birthdays are highlighted at the top of the page." },
        { title: "Former team members", body: 'Use the "Former team members" tab to access archive of those who left.' },
      ] },
  },
  "org-chart": {
    pt: { title: "Organograma",
      steps: [
        { title: "Árvore expansível", body: "Use Expand all / Collapse all para abrir ou fechar todos os ramos de uma vez." },
        { title: "Buscar", body: "Use a busca por nome ou cargo no topo direito para localizar alguém rapidamente." },
        { title: "Export PDF", body: "Clique em Export PDF para baixar uma versão imprimível com header Sagewood." },
        { title: "Perfil", body: "Clique em um nó para abrir o perfil completo da pessoa." },
      ] },
    en: { title: "Org Chart",
      steps: [
        { title: "Expandable tree", body: "Use Expand all / Collapse all to open or close every branch at once." },
        { title: "Search", body: "Use the search box at top-right to find someone by name or title." },
        { title: "Export PDF", body: "Click Export PDF to download a printable version with Sagewood header." },
        { title: "Profile", body: "Click a node to open the full team member profile." },
      ] },
  },
  performance: {
    pt: { title: "Performance",
      steps: [
        { title: "Ciclos", body: "Veja ciclos de avaliação ativos. Clique em um ciclo para abrir suas avaliações pendentes." },
        { title: "Self vs Manager", body: 'Como manager, você vê a auto-avaliação do integrante na mesma tela ao preencher sua avaliação. Use o filtro "My team".' },
        { title: "PIPs", body: "Planos de melhoria (PIPs) ativos aparecem na tab dedicada com status e datas." },
      ] },
    en: { title: "Performance",
      steps: [
        { title: "Cycles", body: "See active evaluation cycles. Click a cycle to open your pending evaluations." },
        { title: "Self vs Manager", body: 'As manager, you see the team member\'s self-evaluation alongside yours. Use the "My team" filter.' },
        { title: "PIPs", body: "Active improvement plans appear on the dedicated tab with status and dates." },
      ] },
  },
  hiring: {
    pt: { title: "Hiring (Kanban)",
      steps: [
        { title: "Vagas", body: "Cada coluna é uma vaga aberta. Cards são candidatos progredindo pelo funil." },
        { title: "Estágios", body: "Aplicado → Screening → Entrevista → Oferta → Assinada. Mova cards arrastando." },
        { title: "Contratar", body: "Ao chegar em Offer signed, clique Hire para criar conta + onboarding completo (diagrama do depto, quiz, treinos mandatórios)." },
      ] },
    en: { title: "Hiring (Kanban)",
      steps: [
        { title: "Vacancies", body: "Each column is an open vacancy. Cards are applicants moving through the funnel." },
        { title: "Stages", body: "Applied → Screening → Interview → Offer → Signed. Drag cards to advance." },
        { title: "Hire", body: "Once at Offer signed, click Hire to create the account + full onboarding (dept diagram, quiz, mandatory trainings)." },
      ] },
  },
  offboarding: {
    pt: { title: "Offboarding",
      steps: [
        { title: "Iniciar termination", body: "Crie um processo informando data efetiva, motivo e tipo. Gera checklist automático." },
        { title: "Checklist", body: "DocuSign, FrankCrum unenroll, equipment return — cada item é fechável pelo RH ou IT responsável." },
        { title: "Arquivar", body: 'Ao completar, o user vira "Former team member" e fica visível no Diretório > tab Former.' },
      ] },
    en: { title: "Offboarding",
      steps: [
        { title: "Start termination", body: "Create a process informing effective date, reason and type. Auto-generates checklist." },
        { title: "Checklist", body: "DocuSign, FrankCrum unenroll, equipment return — each item closable by HR or IT owner." },
        { title: "Archive", body: 'On completion, the user becomes a "Former team member" visible in Directory > Former tab.' },
      ] },
  },
  approvals: {
    pt: { title: "Aprovações pendentes",
      steps: [
        { title: "Sua caixa", body: "Centraliza PTO, reembolsos, team member expenses, loans e salary moves aguardando sua decisão." },
        { title: "Detalhe", body: "Clique em qualquer item para abrir a modal com motivo, anexos e violações de política (se houver)." },
        { title: "Decidir", body: "Aprove ou rejeite com um comentário opcional. A decisão é registrada no histórico do solicitante." },
      ] },
    en: { title: "Pending approvals",
      steps: [
        { title: "Your inbox", body: "Centralises PTO, reimbursements, team member expenses, loans and salary moves awaiting your decision." },
        { title: "Detail", body: "Click any item to open the modal with reason, attachments and policy violations (if any)." },
        { title: "Decide", body: "Approve or reject with an optional comment. The decision is recorded in the requester's history." },
      ] },
  },
  loans: {
    pt: { title: "Empréstimos (confidencial)",
      steps: [
        { title: "Só RH cria", body: "Por confidencialidade, apenas o RH registra empréstimos. Integrantes só veem seu próprio (se registrado)." },
        { title: "Parcelamento", body: "Definir valor + número de parcelas calcula automaticamente o valor mensal e cria o cronograma." },
        { title: "Approval chain", body: "Cada empréstimo passa pelo chain definido em Admin > Approval Workflows." },
      ] },
    en: { title: "Loans (confidential)",
      steps: [
        { title: "HR only creates", body: "For confidentiality, only HR registers loans. Team members only see their own (if registered)." },
        { title: "Installments", body: "Set amount + installments and the monthly value + schedule are auto-calculated." },
        { title: "Approval chain", body: "Each loan flows through the chain configured under Admin > Approval Workflows." },
      ] },
  },
  documents: {
    pt: { title: "Documentos",
      steps: [
        { title: "Biblioteca", body: "Encontra políticas, manuais, contratos modelo, formulários internos." },
        { title: "Download direto", body: "Clique no link de cada documento para abrir ou baixar." },
      ] },
    en: { title: "Documents",
      steps: [
        { title: "Library", body: "Find policies, manuals, model contracts, internal forms." },
        { title: "Direct download", body: "Click the link on each document to open or download." },
      ] },
  },
  knowledge: {
    pt: { title: "Knowledge Base",
      steps: [
        { title: "Base de conhecimento", body: "Repositório de Q&A verificadas, links SharePoint e documentos referência." },
        { title: "Buscar", body: "Use a busca para encontrar respostas rápidas. Filtre por categoria." },
        { title: "Sage Copilot", body: "Se não achar, pergunte ao Sage Copilot (Claude Sonnet 4.5) — a resposta inclui fontes citadas." },
      ] },
    en: { title: "Knowledge Base",
      steps: [
        { title: "Knowledge repository", body: "Repository of verified Q&As, SharePoint links and reference documents." },
        { title: "Search", body: "Use the search bar to find quick answers. Filter by category." },
        { title: "Sage Copilot", body: "If you don't find it, ask Sage Copilot (Claude Sonnet 4.5) — answers include cited sources." },
      ] },
  },
  copilot: {
    pt: { title: "Sage Copilot",
      steps: [
        { title: "Pergunte naturalmente", body: 'Digite qualquer pergunta em PT ou EN: políticas, saldos, procedimentos, contatos…' },
        { title: "Sessions", body: "Cada conversa é uma session. Você pode voltar a sessions antigas pela lateral." },
        { title: "Sources & feedback", body: "As respostas citam fontes da Knowledge Base. Use os polegares ↑↓ pra ajudar o sistema a melhorar." },
      ] },
    en: { title: "Sage Copilot",
      steps: [
        { title: "Ask naturally", body: "Type any question in EN or PT: policies, balances, procedures, contacts…" },
        { title: "Sessions", body: "Each conversation is a session. You can revisit old sessions from the sidebar." },
        { title: "Sources & feedback", body: "Answers cite Knowledge Base sources. Use the ↑↓ thumbs to help the system improve." },
      ] },
  },
  departments: {
    pt: { title: "Department Hub",
      steps: [
        { title: "Cards de departamentos", body: "Cada card mostra o departamento e se há diagrama configurado. Cards com ✓ verde indicam que você passou no quiz daquele depto." },
        { title: "Diagrama", body: "Clique no card para ler o diagrama (texto/imagem/PDF) e entender como o departamento opera." },
        { title: "Quiz", body: "Departamentos com quiz exigem que novos integrantes passem como parte do onboarding." },
      ] },
    en: { title: "Department Hub",
      steps: [
        { title: "Department cards", body: "Each card shows the department and whether a diagram exists. Cards with green ✓ mean you passed that dept's quiz." },
        { title: "Diagram", body: "Click a card to read the diagram (text/image/PDF) and understand how the department operates." },
        { title: "Quiz", body: "Departments with a quiz require new hires to pass it as part of onboarding." },
      ] },
  },
  "salary-matrix": {
    pt: { title: "Salary Matrix (confidencial)",
      steps: [
        { title: "Matriz Tracks × Levels", body: "Linhas são funções (Engineering, Operations…), colunas são níveis (I, II, III, Lead). Cada casinha tem banda salarial + bônus alvo." },
        { title: "Mover integrante", body: "Clique em um pill (occupant ou unplaced) para abrir a modal de movimentação. Defina cell + novo salário + motivo." },
        { title: "Approval chain", body: "Se houver chain salary_move ativa, a movimentação entra pendente e precisa de aprovação sequencial (ex.: RH → CEO)." },
        { title: "Histórico", body: "Cada move cria entrada em salary_history com from/to, reason e quem aprovou. Hover no pill > clique no ícone history." },
      ] },
    en: { title: "Salary Matrix (confidential)",
      steps: [
        { title: "Tracks × Levels grid", body: "Rows are job functions (Engineering, Operations…), columns are career levels (I, II, III, Lead). Each cell has salary band + target bonus." },
        { title: "Move team member", body: "Click a pill (occupant or unplaced) to open the move modal. Pick cell + new salary + reason." },
        { title: "Approval chain", body: "If a salary_move chain is active, the move goes pending and needs sequential approval (e.g. HR → CEO)." },
        { title: "History", body: "Every move creates a salary_history entry with from/to, reason and who approved. Hover the pill > click history icon." },
      ] },
  },
  admin: {
    pt: { title: "Admin & RH",
      steps: [
        { title: "Configurações", body: "Edite cost codes, departamentos, integrações, Approval Workflows, política de Travel, regras de notificação." },
        { title: "Demo & Manual", body: "Gere arquivos demonstrativos (PPTX/PDF) ou recarregue dados de showroom para o trial." },
        { title: "Notification Rules", body: "Defina thresholds corporativos (D-7/D-3 lembretes, escalação manager/RH, master switch de e-mail)." },
      ] },
    en: { title: "Admin & HR",
      steps: [
        { title: "Settings", body: "Edit cost codes, departments, integrations, Approval Workflows, Travel policy, notification rules." },
        { title: "Demo & Manual", body: "Generate demo files (PPTX/PDF) or reload showroom data for the trial." },
        { title: "Notification Rules", body: "Set corporate thresholds (D-7/D-3 reminders, manager/HR escalation, e-mail master switch)." },
      ] },
  },
  profile: {
    pt: { title: "Meu perfil",
      steps: [
        { title: "Dados pessoais", body: "Atualize telefone, e-mail pessoal, contato de emergência, bio e foto." },
        { title: "Documentos", body: "Acesse seus contratos, comprovantes, formulários assinados." },
      ] },
    en: { title: "My profile",
      steps: [
        { title: "Personal data", body: "Update phone, personal e-mail, emergency contact, bio and photo." },
        { title: "Documents", body: "Access your contracts, receipts, signed forms." },
      ] },
  },
};

export function getTour(pageKey, lang = "pt", role = null) {
  const page = T[pageKey];
  if (!page) return null;
  // Persona-aware lookup (currently used for `travel` in EN): if a role is
  // supplied and the page has a `<lang>_<role>` bucket, return that. Otherwise
  // fall back to the plain `<lang>` bucket, then to `pt`.
  if (lang && role) {
    const personaKey = `${lang}_${role}`;
    if (page[personaKey]) return page[personaKey];
    // HR / admin / CEO all see the HR flow (they all close the loop in QBO).
    if (lang === "en" && (role === "admin" || role === "ceo") && page.en_hr) return page.en_hr;
  }
  return page[lang] || page.pt || null;
}

export const PAGE_KEYS = Object.keys(T);
